import { Component, type ErrorInfo, type ReactNode } from "react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { logger } from "@/services/logger";
type DisplayProps = {
  children: ReactNode;
  resetKey: unknown;
  onReload: () => Promise<void>;
  isReloading: boolean;
  isCoolingDown: boolean;
};
class DisplayBoundary extends Component<
  DisplayProps,
  { failed: boolean; resetKey: unknown }
> {
  constructor(props: DisplayProps) {
    super(props);
    this.state = { failed: false, resetKey: props.resetKey };
  }
  static getDerivedStateFromError() {
    return { failed: true };
  }
  static getDerivedStateFromProps(
    props: DisplayProps,
    state: { resetKey: unknown }
  ) {
    return props.resetKey === state.resetKey
      ? null
      : { failed: false, resetKey: props.resetKey };
  }
  componentDidCatch(error: Error, info: ErrorInfo) {
    logger.error("Shipment rows could not render", error, info.componentStack);
  }
  render() {
    return this.state.failed ? (
      <Alert variant="destructive">
        <AlertDescription>
          Shipment view could not be displayed. Filters and current input are
          kept.
          <Button
            type="button"
            variant="outline"
            className="ml-2"
            disabled={this.props.isReloading || this.props.isCoolingDown}
            onClick={() => void this.props.onReload()}
          >
            {this.props.isCoolingDown ? "Please wait" : "Reload shipments"}
          </Button>
        </AlertDescription>
      </Alert>
    ) : (
      this.props.children
    );
  }
}
/** The callback executes in a child below the boundary, never in its parent or fallback. */
function ShipmentDisplay({
  renderDisplay,
}: {
  renderDisplay: () => ReactNode;
}) {
  return <>{renderDisplay()}</>;
}
export function ShipmentReadBoundary({
  renderDisplay,
  resetKey,
  onReload,
  isReloading,
  isCoolingDown,
}: {
  renderDisplay: () => ReactNode;
  resetKey: unknown;
  onReload: () => Promise<void>;
  isReloading: boolean;
  isCoolingDown: boolean;
}) {
  // These reads are explicit query state, not thrown/Suspense queries. The owner
  // awaits its scoped refetch before requesting a display reset.
  return (
    <DisplayBoundary
      resetKey={resetKey}
      onReload={onReload}
      isReloading={isReloading}
      isCoolingDown={isCoolingDown}
    >
      <ShipmentDisplay renderDisplay={renderDisplay} />
    </DisplayBoundary>
  );
}
